import React from "react";
import agent from "../../../agent";
import {
  buildBandColumnKey,
  buildCarrierGroups,
  buildDirtyPatchPayload,
} from "./matrixAdapters";
import { buildCommitRequestFromPreview } from "./importAdapters";
import {
  buildNextDirtyRowsById,
  buildVisibleTariffRows,
} from "./tariffMatrixState";
import {
  CROSS_DOCKING_REGISTRY_MOCK,
  getCrossDockingDetailMock,
  getCrossDockingTariffGridMock,
} from "./mockData";

const REGISTRY_PAGE = 0;
const REGISTRY_PAGE_SIZE = 1000;

const normalizeRegistryRows = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.content)) {
    return payload.content;
  }

  return [];
};

const normalizeRegistryRow = (row) => ({
  ...row,
  id: row?.id ?? row?.crossDockingPointId ?? row?.kato,
});

const mergeUpdatedTariffsIntoRows = (rows = [], updated = []) => {
  if (!updated.length) {
    return rows;
  }

  const updatedByColumnKey = new Map(
    updated.map((item) => [
      `${item.crossDockingPointId}:${buildBandColumnKey(item)}`,
      item,
    ])
  );

  return rows.map((row) => {
    const nextCells = Object.entries(row.cells || {}).reduce((accumulator, [columnKey, cell]) => {
      const updatedCell = updatedByColumnKey.get(`${row.crossDockingPointId}:${columnKey}`);

      accumulator[columnKey] = updatedCell
        ? {
            ...cell,
            price: updatedCell.price,
            updatedAt: updatedCell.updatedAt,
            draftPrice: undefined,
          }
        : cell;

      return accumulator;
    }, {});

    return {
      ...row,
      cells: nextCells,
    };
  });
};

export const useCarrierTariffMatrix = () => {
  const [registryRows, setRegistryRows] = React.useState([]);
  const [carrierGroups, setCarrierGroups] = React.useState([]);
  const [activeCarrier, setActiveCarrier] = React.useState(null);
  const [tariffCache, setTariffCache] = React.useState({});
  const [dirtyRowsById, setDirtyRowsById] = React.useState({});
  const [isLoadingRegistry, setIsLoadingRegistry] = React.useState(false);
  const [isLoadingTariffs, setIsLoadingTariffs] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [importStage, setImportStage] = React.useState("idle");
  const [importPreview, setImportPreview] = React.useState(null);

  const refreshCarrierGroups = React.useCallback((rows) => {
    const groups = buildCarrierGroups(rows.map(normalizeRegistryRow));
    setCarrierGroups(groups);
    setActiveCarrier((current) =>
      groups.some((group) => group.key === current) ? current : groups[0]?.key || null
    );
    return groups;
  }, []);

  const loadRegistry = React.useCallback(async () => {
    setIsLoadingRegistry(true);
    try {
      const payload = await agent.CrossDocking.getRegistry(REGISTRY_PAGE, REGISTRY_PAGE_SIZE);
      const backendRows = normalizeRegistryRows(payload).map(normalizeRegistryRow);
      const rows = backendRows.length ? backendRows : CROSS_DOCKING_REGISTRY_MOCK.map(normalizeRegistryRow);

      setRegistryRows(rows);
      refreshCarrierGroups(rows);
      return { usedMock: !backendRows.length };
    } catch (error) {
      const rows = CROSS_DOCKING_REGISTRY_MOCK.map(normalizeRegistryRow);
      setRegistryRows(rows);
      refreshCarrierGroups(rows);
      return { usedMock: true, error };
    } finally {
      setIsLoadingRegistry(false);
    }
  }, [refreshCarrierGroups]);

  const loadTariffsForCarrier = React.useCallback(async (carrierKey, options = {}) => {
    if (!carrierKey) {
      return { usedMock: false };
    }

    if (!options.force && tariffCache[carrierKey]) {
      return { usedMock: false, skipped: true };
    }

    const group = carrierGroups.find((item) => item.key === carrierKey);
    const pointIds = group?.crossDockingPointIds || [];

    setIsLoadingTariffs(true);
    try {
      const payload = pointIds.length
        ? await agent.CrossDocking.getTariffGrid(pointIds)
        : { bands: [], rows: [] };
      const nextPayload = Array.isArray(payload?.rows) && payload.rows.length
        ? payload
        : getCrossDockingTariffGridMock(pointIds);

      setTariffCache((current) => ({
        ...current,
        [carrierKey]: nextPayload,
      }));
      return { usedMock: nextPayload !== payload };
    } catch (error) {
      const fallbackPayload = getCrossDockingTariffGridMock(pointIds);
      setTariffCache((current) => ({
        ...current,
        [carrierKey]: fallbackPayload,
      }));
      return { usedMock: true, error };
    } finally {
      setIsLoadingTariffs(false);
    }
  }, [carrierGroups, tariffCache]);

  const loadMissingAddressesForCarrier = React.useCallback(async (carrierKey) => {
    const group = carrierGroups.find((item) => item.key === carrierKey);
    const rowsNeedingAddress = (group?.rows || []).filter((row) => !row?.address);

    if (!rowsNeedingAddress.length) {
      return;
    }

    const detailResults = await Promise.all(
      rowsNeedingAddress.map(async (row) => {
        try {
          const detail = await agent.CrossDocking.getDetail(row.id);
          return { id: row.id, address: detail?.address || row.address || "" };
        } catch (error) {
          const detail = getCrossDockingDetailMock(row.id);
          return { id: row.id, address: detail?.address || "" };
        }
      })
    );

    setRegistryRows((currentRows) =>
      {
        const nextRows = currentRows.map((row) => {
        const matchedDetail = detailResults.find((detail) => detail.id === row.id);
        return matchedDetail ? { ...row, address: matchedDetail.address } : row;
        });

        refreshCarrierGroups(nextRows);
        return nextRows;
      }
    );
  }, [carrierGroups, refreshCarrierGroups]);

  const activeGroup = React.useMemo(
    () => carrierGroups.find((item) => item.key === activeCarrier) || null,
    [activeCarrier, carrierGroups]
  );

  const activeTariffPayload = activeCarrier ? tariffCache[activeCarrier] : null;

  const rows = React.useMemo(() => {
    return buildVisibleTariffRows({
      registryRows: activeGroup?.rows || [],
      tariffRows: activeTariffPayload?.rows || [],
      bands: activeTariffPayload?.bands || [],
      dirtyRowsById,
    });
  }, [activeGroup, activeTariffPayload, dirtyRowsById]);

  const updateCellDraft = React.useCallback((row, columnKey, value) => {
    setDirtyRowsById((current) => buildNextDirtyRowsById(current, row, columnKey, value));
  }, []);

  const refreshActiveCarrierData = React.useCallback(async () => {
    const registryResult = await loadRegistry();

    if (activeCarrier) {
      await loadTariffsForCarrier(activeCarrier, { force: true });
      await loadMissingAddressesForCarrier(activeCarrier);
    }

    return registryResult;
  }, [activeCarrier, loadMissingAddressesForCarrier, loadRegistry, loadTariffsForCarrier]);

  const createTariffBand = React.useCallback(async ({ weight, nextWeight }) => {
    if (!activeCarrier) {
      throw new Error("Carrier is required");
    }

    await agent.CrossDocking.createTariffBand({
      carrierType: activeCarrier,
      weight,
      nextWeight,
    });

    return refreshActiveCarrierData();
  }, [activeCarrier, refreshActiveCarrierData]);

  const createTariffRow = React.useCallback(async ({ katoOption }) => {
    if (!activeCarrier) {
      throw new Error("Carrier is required");
    }

    const kato = katoOption?.kato || katoOption?.code || katoOption?.value;
    const locality = katoOption?.nameRu || katoOption?.label || katoOption?.name || "";

    await agent.CrossDocking.createTariffRow({
      carrierType: activeCarrier,
      kato,
      locality,
    });

    return refreshActiveCarrierData();
  }, [activeCarrier, refreshActiveCarrierData]);

  const deleteTariffRow = React.useCallback(async (row) => {
    await agent.CrossDocking.deleteTariffRow(row.crossDockingPointId, row.kato);
    return refreshActiveCarrierData();
  }, [refreshActiveCarrierData]);

  const searchKato = React.useCallback(async (query) => {
    const payload = await agent.Kato.searchGraph(query, 0, 20);
    if (Array.isArray(payload)) {
      return payload;
    }
    if (Array.isArray(payload?.content)) {
      return payload.content;
    }
    return [];
  }, []);

  const saveDirtyRows = React.useCallback(async () => {
    const dirtyRows = Object.values(dirtyRowsById).map((row) => ({
      ...row,
      changes: Object.values(row.changes || {}),
    }));

    if (!dirtyRows.length) {
      return { updated: [], rejected: [] };
    }

    setIsSaving(true);
    try {
      const result = await agent.CrossDocking.patchTariffRows(buildDirtyPatchPayload(dirtyRows));
      const updatedRows = Array.isArray(result?.updated) ? result.updated : [];
      const rejectedRows = Array.isArray(result?.rejected) ? result.rejected : [];

      setTariffCache((current) => {
        if (!activeCarrier || !current[activeCarrier]) {
          return current;
        }

        return {
          ...current,
          [activeCarrier]: {
            ...current[activeCarrier],
            rows: mergeUpdatedTariffsIntoRows(current[activeCarrier].rows, updatedRows),
          },
        };
      });

      setDirtyRowsById((current) => {
        const nextState = { ...current };
        updatedRows.forEach((item) => {
          delete nextState[item.crossDockingPointId];
        });
        return nextState;
      });

      return {
        updated: updatedRows,
        rejected: rejectedRows,
      };
    } finally {
      setIsSaving(false);
    }
  }, [activeCarrier, dirtyRowsById]);

  const startImportPreview = React.useCallback(async (file) => {
    if (Object.keys(dirtyRowsById).length > 0) {
      throw new Error("У вас есть несохранённые правки. Сохраните или отмените их перед импортом.");
    }
    if (!activeCarrier) {
      throw new Error("Не выбран перевозчик.");
    }
    setImportStage("uploading");
    try {
      const preview = await agent.CrossDocking.importTariffPreview(activeCarrier, file);
      setImportPreview(preview);
      setImportStage("preview");
      return preview;
    } catch (error) {
      setImportStage("idle");
      throw error;
    }
  }, [activeCarrier, dirtyRowsById]);

  const commitImport = React.useCallback(async ({ approvedNewBandKeys } = {}) => {
    if (!importPreview || !activeCarrier) {
      throw new Error("Нет предварительного просмотра для подтверждения.");
    }
    const body = buildCommitRequestFromPreview(importPreview, {
      carrierType: activeCarrier,
      approvedNewBandKeys,
    });
    setImportStage("committing");
    try {
      const result = await agent.CrossDocking.importTariffCommit(body);
      setImportPreview(null);
      setImportStage("idle");
      await loadRegistry();
      await loadTariffsForCarrier(activeCarrier);
      return result;
    } catch (error) {
      setImportStage("preview");
      throw error;
    }
  }, [activeCarrier, importPreview, loadRegistry, loadTariffsForCarrier]);

  const clearImportPreview = React.useCallback(() => {
    setImportPreview(null);
    setImportStage("idle");
  }, []);

  const setActiveBulk = React.useCallback(async (changes) => {
    const targetIds = new Set(changes.map((c) => c.crossDockingPointId));
    const patch = new Map(changes.map((c) => [c.crossDockingPointId, c.isActive]));

    setRegistryRows((prev) => {
      const nextRows = prev.map((row) =>
        targetIds.has(row.id) ? { ...row, isActive: patch.get(row.id) } : row
      );
      refreshCarrierGroups(nextRows);
      return nextRows;
    });

    try {
      return await agent.CrossDocking.setActiveBulk(changes);
    } catch (error) {
      setRegistryRows((prev) => {
        const revertedRows = prev.map((row) =>
          targetIds.has(row.id) ? { ...row, isActive: !patch.get(row.id) } : row
        );
        refreshCarrierGroups(revertedRows);
        return revertedRows;
      });
      throw error;
    }
  }, [refreshCarrierGroups]);

  return {
    activeCarrier,
    bands: activeTariffPayload?.bands || [],
    carrierGroups,
    clearImportPreview,
    commitImport,
    createTariffBand,
    createTariffRow,
    deleteTariffRow,
    dirtyCount: Object.keys(dirtyRowsById).length,
    importPreview,
    importStage,
    isLoadingRegistry,
    isLoadingTariffs,
    isSaving,
    loadMissingAddressesForCarrier,
    loadRegistry,
    loadTariffsForCarrier,
    rows,
    saveDirtyRows,
    searchKato,
    setActiveBulk,
    setActiveCarrier,
    startImportPreview,
    updateCellDraft,
  };
};
